import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import { ColumnFlexCentered, FlexCentered } from '~/components/common/containers'
import { translate } from '~/core/translate'
import { getPriceString, numToHumanReadable } from '../../core/utils'

import PlanButton from './plan-button'
import { LabelSemibold, labelSemiboldCss, TitleH3, TitleH4 } from '../typography'
import { MAX_MOBILE_THRESHOLD } from '~/core/constants'
import MauTooltip from './mau-tooltip'

const PlanCard = styled(ColumnFlexCentered)`
  border-radius: ${({ isPopular }) => isPopular ? '0 0 8px 8px' : '8px'};
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.08), 0px 8px 24px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  height: 248px;
  padding: 16px;
  position: relative;
  text-align: center;
  width: 260px;

  ${props =>
    props.isActive
      ? `
    background: #242528;
    color: white;`
      : `
    background: #FFF;
    color: #1C2025;
  `}

  ${TitleH4} {
    margin-bottom: 14px;
  }
`

const Name = styled.div`
  ${labelSemiboldCss};
  margin-bottom: 8px;

  color: ${props => (props.isActive ? '#DEDFE2' : '#5E6D7A')};
`

const Section = styled.div`
  margin-bottom: 8px;
`

const PriceBox = styled(FlexCentered)`
  background: ${props => (props.isActive ? '#4D5057' : '#F7F8FA')};
  border-radius: 3px;
  box-sizing: border-box;
  color: ${props => (props.isActive ? '#FFF' : '#000')};
  flex-shrink: 0;
  font-size: 14px;
  height: 56px;
  letter-spacing: -0.006em;
  line-height: 20px;
  margin-bottom: 8px;
  padding: 8px;
  place-content: center;
  width: 100%;
`

const Popular = styled.div`
  ${labelSemiboldCss};
  background: #24A148;
  border-radius: 8px 8px 0px 0px;
  color: #fff;
  padding: 8px 0;
  position: absolute;
  top: -32px;
  width: 100%;
`

const BoxShadowContainer = styled.div`
  padding: 56px 0 24px 24px;
  scroll-snap-align: start;

  &:last-child {
    padding-right: 24px;
  }

  @media screen and (min-width: ${MAX_MOBILE_THRESHOLD}) {
    padding: 56px 0 24px 16px;

    &:first-child {
      padding-left: 0;
    }

    ${PlanCard} {
      width: 212px;
    }

    &:last-child {
      padding-right: 16px;
    }
  }
`

const Plan = ({
  id,
  name,
  users,
  totalCost,
  isActive,
  isPopular,
  currency,
  selectPlan,
  costLimit,
  popularText,
  shouldContactSales
}) => (
  <BoxShadowContainer>
    <PlanCard fullWidth isActive={isActive} isPopular={isPopular}>
      {isPopular && <Popular>{popularText}</Popular>}

      <Name isActive={isActive}>{name}</Name>

      {shouldContactSales
        ? <TitleH4>{translate('more_users')}</TitleH4>
        : (
          <>
            <Section>
              <TitleH3>
                {numToHumanReadable(users, costLimit)}
              </TitleH3>
            </Section>
            <Section>
              <MauTooltip>
                <LabelSemibold>{translate('mau')}</LabelSemibold>
              </MauTooltip>
            </Section>
          </>
          )}

      <PriceBox isActive={isActive}>
        {shouldContactSales && (
          translate('custom_plan')
        )}

        {!shouldContactSales && (
          <>
            {totalCost > 0
              ? (
                <>
                  <b>{getPriceString(currency, totalCost)}</b>/{translate('month')}
                </>
                )
              : <b>{translate('free')}</b>}
          </>
        )}
      </PriceBox>

      <PlanButton
        isActive={isActive}
        selectPlan={selectPlan}
        id={id}
        shouldContactSales={shouldContactSales}
        name={name}
      />
    </PlanCard>
  </BoxShadowContainer>
)

Plan.propTypes = {
  name: PropTypes.string,
  users: PropTypes.number,
  totalCost: PropTypes.number,
  costLimit: PropTypes.number,
  mauOverage: PropTypes.number,
  isActive: PropTypes.bool,
  isPopular: PropTypes.bool,
  currency: PropTypes.string,
  selectPlan: PropTypes.func,
  id: PropTypes.string,
  popularText: PropTypes.string,
  shouldContactSales: PropTypes.bool
}

export default Plan
